import { cn } from '../../lib/cn';
import { haptic } from '../../lib/tma';

export interface SegmentedProps<T extends string> {
  value: T;
  /** `mark`: a small word under the label (e.g. «совет»). */
  options: ReadonlyArray<{ value: T; label: string; mark?: string }>;
  onChange(value: T): void;
  className?: string;
}

/**
 * Segmented switch: a sunken track, the chosen segment raised as a card. More
 * than three segments go in two columns: four long labels do not fit a phone row.
 */
export function Segmented<T extends string>({ value, options, onChange, className }: SegmentedProps<T>) {
  return (
    <div
      role="tablist"
      className={cn(options.length > 3 ? 'grid grid-cols-2' : 'flex', 'gap-1 p-1 rounded-xl bg-surface-2', className)}
    >
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => {
              if (selected) return;
              haptic('light');
              onChange(o.value);
            }}
            className={cn(
              'flex-1 min-h-9 px-2 rounded-lg text-sm transition-colors duration-150 ease-smooth',
              o.mark && 'flex flex-col items-center justify-center py-1 leading-tight',
              selected ? 'bg-surface shadow-card text-fg font-semibold' : 'text-hint font-medium',
            )}
          >
            {o.label}
            {o.mark && <span className="text-[10px] font-medium text-hint leading-none mt-0.5">{o.mark}</span>}
          </button>
        );
      })}
    </div>
  );
}
